import { ArchiveIcon, CartIcon, CopyIcon, EyeIcon, PenIcon } from "@xola/icons";
import { flash } from "@xola/ui-kit";
import clsx from "clsx";
import { isPartnerPackageProduct } from "common";
import React from "react";
import { useMutation } from "react-query";
import { Button, ButtonType, CheckoutType, Product, ProductObject } from "types";
import { NoTranslate } from "../../Common/NoTranslate";
import { ProductImage } from "../../Common/ProductImage";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { PartnerBadge, PartnerSellerBadge } from "./PartnerBadge";
import { api } from "@/api/api";
import { InlineConfirmDeletePopover } from "@/components/Common/InlineConfirmDeletePopover";
import { useSeller, useUser } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getButtonCode, getButtonUrl, isX2CheckoutSupported, openCheckoutIframe } from "@/models/ButtonCode";
import { isXolaAdmin } from "@/models/User";

interface ButtonItemProps {
    type?: ButtonType;
    button?: Button;
    product?: Product;
    checkoutType: string;
    popupType: string;
    onClick?: () => void;
}

export const ButtonItem = ({ type, button, product, checkoutType, popupType, onClick }: ButtonItemProps) => {
    const user = useUser();
    const seller = useSeller();
    const showX2Checkout = isX2CheckoutSupported(button, product);
    const customType = showX2Checkout ? `${type}_x2` : type;
    const buttonCode = getButtonCode(checkoutType, popupType, customType, product, button);
    const autoApplyCode = button?.autoApplyCode;
    const name = product?.name ?? button?.name;
    const isPartnerProduct = product?.object === ProductObject.EXPERIENCE && product?.seller.id !== seller.id;
    const isPartnerPackage = isPartnerPackageProduct(product);
    const partnerSellerId = isPartnerProduct || isPartnerPackage ? product?.seller.id : null;

    const invalidate = useInvalidate();

    const { mutateAsync: archiveButton, isLoading } = useMutation(async (buttonId: ID) => {
        await api.delete(`/buttons/${buttonId}`);
        invalidate("buttons", buttonId);
        flash.success("Successfully archived button");
    });

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(buttonCode);
        flash.success("Copied to Clipboard");
    };

    const handlePreviewClick = () => {
        const url = getButtonUrl(seller, checkoutType, type, product, button);
        if (url) {
            window.open(url, "_blank");
        }
    };

    const handleX2PreviewClick = () => {
        const url = getButtonUrl(seller, checkoutType, String(type) + "_x2", product, button);
        if (checkoutType === CheckoutType.URL && url) {
            window.open(url, "_blank");
        } else {
            openCheckoutIframe(url);
        }
    };

    const handleArchiveClick = async (buttonId: ID) => {
        await archiveButton(buttonId);
        invalidate("buttons", buttonId);
    };

    return (
        <div className="flex items-center justify-between space-x-2 border-b border-b-gray-light py-5 leading-p1 tracking-tightest">
            <div
                className={clsx(
                    "flex items-center justify-start space-x-2",
                    type === ButtonType.CHECKOUT ? "w-4/12" : "w-6/12",
                )}
            >
                {product && type === ButtonType.SINGLE && (
                    <ProductImage size="medium" className="mr-0 rounded-lg" product={product} />
                )}
                {name ? (
                    <div className="flex flex-col items-start space-y-2">
                        <NoTranslate as="p" className="truncate whitespace-normal text-base font-bold">
                            {name}
                        </NoTranslate>
                        {isPartnerProduct || isPartnerPackage ? (
                            <div className="flex items-center gap-2">
                                <PartnerBadge isPackage={isPartnerPackage} />
                                {partnerSellerId && <PartnerSellerBadge sellerId={partnerSellerId} />}
                            </div>
                        ) : null}
                        {autoApplyCode && (
                            <p className="truncate whitespace-normal font-normal">{autoApplyCode.toUpperCase()}</p>
                        )}
                    </div>
                ) : (
                    <p className="truncate whitespace-normal text-md font-bold">All Products</p>
                )}
            </div>
            <NoTranslate as="div" className="flex w-5/12 justify-center break-all font-mono text-sm text-gray-darker">
                {buttonCode.includes("http") ? (
                    <a href={buttonCode} target="_blank" rel="noreferrer">
                        {buttonCode}
                    </a>
                ) : (
                    buttonCode
                )}
            </NoTranslate>
            <div className="flex items-center justify-end space-x-1">
                {showX2Checkout && (
                    <UIButton
                        data-testid="button-item-x2-preview-button"
                        title="Preview in X2 Checkout"
                        onClick={handleX2PreviewClick}
                    >
                        <CartIcon />
                    </UIButton>
                )}

                {/* Show the V1 Preview if the user is a Xola admin so they can compare V1 & X2 */}
                {(isXolaAdmin(user) || !showX2Checkout) && (
                    <UIButton data-testid="button-item-preview-button" title="Preview" onClick={handlePreviewClick}>
                        <EyeIcon />
                    </UIButton>
                )}
                {button && (
                    <UIButton data-testid="button-item-edit-button" title="Edit" onClick={onClick}>
                        <PenIcon />
                    </UIButton>
                )}
                <UIButton data-testid="button-item-copy-button" title="Click to copy" onClick={handleCopyToClipboard}>
                    <CopyIcon />
                </UIButton>
                {button && (
                    <InlineConfirmDeletePopover
                        placement="left"
                        confirmButtonText="Confirm"
                        onConfirm={async () => handleArchiveClick(button.id)}
                    >
                        <UIButton title="Archive" disabled={isLoading}>
                            <ArchiveIcon />
                        </UIButton>
                    </InlineConfirmDeletePopover>
                )}
            </div>
        </div>
    );
};

interface UIButtonProps {
    disabled?: boolean;
    title?: string;
    onClick?: () => void;
    children?: React.ReactNode;
}

const UIButton = ({ disabled, title, onClick, children, ...rest }: UIButtonProps) => {
    return (
        <SecondaryButton
            title={title}
            size="small"
            disabled={disabled}
            className="w-7.5 !px-2"
            onClick={onClick}
            {...rest}
        >
            {children}
        </SecondaryButton>
    );
};
